import type { Persister } from '@tanstack/query-persist-client-core';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query';
import superjson from 'superjson';
import { queryKeys } from './keys';

/** localStorage key the dehydrated query cache is written to. */
export const PERSIST_KEY = 'gg-query-cache';

/**
 * Bump this when the shape of persisted query data changes so a deploy
 * discards stale caches instead of rehydrating incompatible data.
 *
 * v2: `bulletinDates` changed from `Set<string>` to `Map<string, handout
 * summary[]>`; a rehydrated v1 Set would crash the Sunday list, which calls
 * `.get()` on it.
 *
 * v3: `TSong` gained a required `hasLyrics` field, and the dehydrate filter
 * became an allowlist that persists only public roots (see PERSISTED_ROOTS),
 * dropping every planner-only collection. Busting forces a returning user's v2
 * blob to be discarded, so any planner-only data already written to disk under
 * v2 (people PII, songs) is flushed rather than rehydrated with a stale shape.
 *
 * v4: `TPublishedBulletin` gained a required `worshipLeader` field (#287).
 *
 * v5: `TPublishedBulletin.sermon.scripture` became a `TScripture` map (#300).
 *
 * v6: `TPublishedBulletin.orderOfService[].scripture` became a `TScripture` map (#301).
 */
export const PERSIST_BUSTER = 'v6';

/** Persisted cache is considered valid for 24h, then discarded on rehydrate. */
export const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24;

/**
 * Query-key roots whose data is public church content, safe to cache in
 * localStorage for offline use (#153). This is an allowlist by design: every
 * other root is planner-only (internal drafts, roster PII, copyrighted lyric
 * text) and is denied by default, so a newly added planner-only collection can
 * never silently leak to disk on a shared workstation. Registering a genuinely
 * public collection is the one case that needs a change here.
 *
 * Published projections live under `bulletins` (including the nested
 * `bulletins/{date}/handouts`), so they persist under that root. Roots come
 * from `queryKeys` so a rename stays in sync instead of silently desyncing a
 * string literal.
 */
const PERSISTED_ROOTS: ReadonlySet<string> = new Set([
  queryKeys.sundays[0], // Sunday list and single-Sunday reads
  queryKeys.sermons[0], // sermon detail
  queryKeys.podcasts[0], // podcast episodes
  queryKeys.bulletins[0], // published bulletins and their published handouts
]);

/**
 * Decides which queries are written to localStorage. Persists only the public
 * content on the allowlist (see PERSISTED_ROOTS); everything else (planner-only
 * drafts, PII, copyrighted text) stays in memory for the session only.
 */
export function shouldDehydrateQuery(query: Query): boolean {
  const root = query.queryKey[0];
  if (typeof root !== 'string' || !PERSISTED_ROOTS.has(root)) {
    return false;
  }
  return defaultShouldDehydrateQuery(query);
}

/**
 * Sync (localStorage) persister for the TanStack Query cache. The cache holds
 * small denormalized text (Sunday list, sermon/podcast metadata), so the
 * synchronous ~5MB localStorage store is sufficient and needs no async IDB.
 *
 * Uses superjson for (de)serialization so Date fields on the cached models
 * (e.g. TSunday.date, TSermon.pubDate) survive the round-trip — plain JSON
 * would turn them into strings and crash date formatting on rehydrate (#153).
 *
 * If localStorage is unavailable (strict privacy mode, disabled cookies, quota
 * exceeded) the try/catch falls back to null and the library treats that as a
 * no-op — the app continues to run in-memory only (#153).
 */
export function createAppPersister(): Persister {
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    // Storage blocked (strict privacy mode / disabled cookies); the query
    // cache simply won't persist — the app still runs in-memory (#153).
    storage = null;
  }
  return createSyncStoragePersister({
    storage,
    key: PERSIST_KEY,
    serialize: superjson.stringify,
    deserialize: superjson.parse,
  });
}
